/**
 * catalogBuilders.ts
 *
 * The catalog-first authoring layer (specs/unified-filters-v2.md Phase 4):
 * turns the existing per-lane option builders (`buildFilterPropertyOptions`,
 * `buildFilterPerspectiveOptions`, `buildFilterRelationshipOptions`) into the
 * `CatalogEntry[]` shape `AspectCatalogMolecule` renders, and mints a
 * canonical filter leaf from a picked entry. See
 * /standards/shared/filtering.md § catalog-first authoring.
 *
 * Deliberately excludes the flat `perspective`/`relationship`/`arc`
 * pseudo-properties `buildFilterPropertyOptions` still emits for legacy
 * callers (the retired property picker) — the catalog offers
 * CONCRETE perspective/relationship instances instead ("Responsible" with
 * `lane:'perspective'`, not a generic "Perspective" row).
 */
import type { CatalogEntry } from '../components/molecules/AspectCatalog/AspectCatalogMolecule';
import type { PropertyOption } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';
import type { FilterExpression, FilterGroupLeaf } from 'types/filters';
import { FILTER_LANE_META, type FilterLaneKind } from '../types/filterLane';
import { defaultOperatorFor } from './filterOperators';
import {
  buildFilterPropertyOptions,
  buildFilterPerspectiveOptions,
  buildFilterRelationshipOptions,
  collectFilterScopeLeaves,
  defaultLaneFilter,
  inferEligibleSchemas,
  type BuildPropertyOptionsConfig,
  type BuildPerspectiveOptionsConfig,
  type BuildRelationshipOptionsConfig,
  type SchemaForFilters,
} from './filterPropertyBuilder';

// Deliberately `any`-optioned — this module's `t` calls never pass a
// `defaultValue`, and its callers hand in several slightly different
// `t` signatures (BuildPropertyOptionsConfig's, BuildPerspectiveOptionsConfig's,
// react-i18next's own), none of which unify under a single strict shape.
type TFn = (key: string, options?: any) => string;

/**
 * The block's group order (`## Design System Reuse Audit` / decomposition
 * table): Identity, Status, Metrics, Dates, then Arcs / Relationships /
 * Perspectives / Measures. `orderCatalogEntries` sorts a mixed batch to this
 * order; the caller's own `recent` bucket always leads regardless
 * (`AspectCatalogMolecule` prepends it unconditionally).
 */
export const CATALOG_GROUP_ORDER: readonly string[] = [
  'identity',
  'status',
  'metrics',
  'dates',
  'arc',
  'relationship',
  'perspective',
  'measure',
];

/** Stable sort of a mixed-lane batch into `CATALOG_GROUP_ORDER`; unknown/ungrouped entries sort last, in place. */
export function orderCatalogEntries(entries: CatalogEntry[]): CatalogEntry[] {
  const rank = (e: CatalogEntry) => {
    const idx = e.group ? CATALOG_GROUP_ORDER.indexOf(e.group) : -1;
    return idx === -1 ? CATALOG_GROUP_ORDER.length : idx;
  };
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => rank(a.entry) - rank(b.entry) || a.index - b.index)
    .map(({ entry }) => entry);
}

/** No `ownerTypes` (or an empty scope) reads as universal — never treated as out of scope. */
function isInScope(ownerTypes: string[] | undefined, scopeTypeSystemNames: string[]): boolean {
  if (!ownerTypes || ownerTypes.length === 0) return true;
  if (scopeTypeSystemNames.length === 0) return true;
  return ownerTypes.some((name) => scopeTypeSystemNames.includes(name));
}

function laneBadge(lane: FilterLaneKind, t: TFn): string {
  return t(FILTER_LANE_META[lane].labelKey);
}

function propertyOptionToCatalogEntry(
  opt: PropertyOption,
  scopeTypeSystemNames: string[],
  isSearching: boolean,
  t: TFn
): CatalogEntry {
  const lane: FilterLaneKind = opt.key === 'type' || opt.key === 'label' ? 'identity' : 'aspect';
  const scoped = !!opt.ownerTypes && opt.ownerTypes.length > 0 && scopeTypeSystemNames.length > 0;
  const inScope = isInScope(opt.ownerTypes, scopeTypeSystemNames);
  return {
    key: opt.key,
    name: opt.label,
    lane,
    badge: t(`filterEditor.catalog.typeBadge.${opt.type}`, { defaultValue: opt.type }),
    type: opt.type,
    group: opt.group,
    groupLabel: opt.groupLabel,
    groupInScope: scoped ? inScope : undefined,
    dim: isSearching && scoped && !inScope,
  };
}

/**
 * Maps an ALREADY-BUILT `PropertyOption[]` onto catalog rows, preserving the
 * caller's own ordering and grouping. Used by the surfaces that pick a single
 * property from a list they assembled themselves (the level sort editor, the
 * Atlas column aspect picker, the column filter's add-a-condition step) — the
 * builders above start from schemas instead, which those callers do not have.
 *
 * Unlike `buildAspectCatalogEntries` this keeps the flat
 * `perspective`/`relationship`/`arc` options a caller may have included, and
 * gives each its own lane glyph rather than filing it under `aspect`.
 */
/** A flat option's lane: its pseudo-type when it is one of the three lane types, identity for `type`/`label`, else aspect. */
function laneForPropertyOption(opt: PropertyOption): FilterLaneKind {
  if (opt.type === 'perspective' || opt.type === 'relationship' || opt.type === 'arc') return opt.type;
  if (opt.key === 'type' || opt.key === 'label') return 'identity';
  return 'aspect';
}

export function propertyOptionsToCatalogEntries(
  options: PropertyOption[],
  t: TFn,
  extras?: { annotations?: Record<string, string> }
): CatalogEntry[] {
  return options.map((opt) => {
    const lane = laneForPropertyOption(opt);
    return {
      key: opt.key,
      name: opt.label,
      lane,
      badge: t(`filterEditor.catalog.typeBadge.${opt.type}`, { defaultValue: opt.type }),
      type: opt.type,
      group: opt.group,
      groupLabel: opt.groupLabel,
      warning: extras?.annotations?.[opt.key],
    };
  });
}

/**
 * Aspect + identity entries (the "Identity / Status / Metrics / Dates" groups
 * plus any per-schema custom groups). Out-of-scope entries (a custom property
 * owned only by types outside `scopeTypeSystemNames`) are ABSENT while
 * browsing (`isSearching: false`) and dimmed with a "not in scope" badge
 * while searching (`isSearching: true`) — spec "out-of-scope on search only".
 */
export function buildAspectCatalogEntries(
  config: BuildPropertyOptionsConfig & { scopeTypeSystemNames?: string[]; isSearching?: boolean }
): CatalogEntry[] {
  const scopeTypeSystemNames = config.scopeTypeSystemNames ?? [];
  const isSearching = config.isSearching ?? false;
  const options = buildFilterPropertyOptions({ ...config, groupBy: 'category' });
  return options
    .filter((opt) => opt.type !== 'perspective' && opt.type !== 'relationship' && opt.type !== 'arc')
    .filter((opt) => isSearching || isInScope(opt.ownerTypes, scopeTypeSystemNames))
    .map((opt) => propertyOptionToCatalogEntry(opt, scopeTypeSystemNames, isSearching, config.t));
}

/** Concrete perspective entries ("Responsible", "Parent (Children)", …) — one row per real perspective, never a generic "Perspective" row. */
export function buildPerspectiveCatalogEntries(config: BuildPerspectiveOptionsConfig): CatalogEntry[] {
  return perspectiveOptionsToCatalogEntries(buildFilterPerspectiveOptions(config), config.t);
}

/**
 * {@link buildPerspectiveCatalogEntries} for a host that already holds its
 * perspective options rather than the schemas they come from (e.g.
 * `LevelFilterPopoverOrganism`'s `availablePerspectives`).
 */
export function perspectiveOptionsToCatalogEntries(
  options: ReadonlyArray<{ systemName: string; label: string }>,
  t: TFn
): CatalogEntry[] {
  return options.map((p) => ({
    key: p.systemName,
    name: p.label,
    lane: 'perspective',
    badge: laneBadge('perspective', t),
    // Every perspective is "who has standing" (advanced-filters-v2.jsx:1284) — a
    // fixed hint, not per-perspective data, since the assignee relation is the
    // same shape for every one of them.
    note: t('filters.hints.perspective'),
    group: 'perspective',
    groupLabel: t('filters.groups.perspective'),
  }));
}

/** Concrete relationship entries — one row per relationship declared on an eligible schema. */
export function buildRelationshipCatalogEntries(config: BuildRelationshipOptionsConfig): CatalogEntry[] {
  return relationshipOptionsToCatalogEntries(buildFilterRelationshipOptions(config), config.t);
}

/** {@link buildRelationshipCatalogEntries} for a host that already holds its relationship options. */
export function relationshipOptionsToCatalogEntries(
  options: ReadonlyArray<{ systemName: string; label: string; description?: string }>,
  t: TFn
): CatalogEntry[] {
  return options.map((r) => ({
    key: r.systemName,
    name: r.label,
    lane: 'relationship',
    badge: laneBadge('relationship', t),
    note: r.description,
    group: 'relationship',
    groupLabel: t('filters.groups.relationship'),
  }));
}

/**
 * The Arc lane's single catalog row — an arc filter correlates
 * selector+health/stage/lifecycle on ONE arc within its own row editor
 * (`arcRowRemainsOneArc`), so unlike perspectives/relationships there is one
 * entry, not one per template. Offered only when at least one eligible
 * schema is arc-enabled.
 */
export function buildArcCatalogEntries(t: TFn, hasArcSchemas: boolean): CatalogEntry[] {
  if (!hasArcSchemas) return [];
  return [
    {
      key: 'arc',
      name: t('filters.properties.arc', { defaultValue: 'Arc' }),
      lane: 'arc',
      badge: laneBadge('arc', t),
      note: t('filters.hints.arc'),
      group: 'arc',
      groupLabel: t('filters.groups.arc'),
    },
  ];
}

/** One aggregate sphere the catalog can mint a locked `measures_into` row against. */
export interface MeasureAggregate {
  id: string;
  name: string;
}

/**
 * Measure entries — one per aggregate sphere reachable via `POST /search`
 * (`measureRole: 'aggregate'`). Absent (returns `[]`) when the fetch failed
 * or found nothing; the caller states the reason (spec:
 * `measuresGroupAbsentWithReasonWhenFetchFails`) rather than showing an empty
 * Measures group.
 */
export function buildMeasureCatalogEntries(aggregates: MeasureAggregate[], t: TFn): CatalogEntry[] {
  return aggregates.map((agg) => ({
    key: agg.id,
    name: agg.name,
    lane: 'measure',
    badge: laneBadge('measure', t),
    group: 'measure',
    groupLabel: t('filters.groups.measure', { defaultValue: 'Measures' }),
  }));
}

/**
 * Mint the canonical filter leaf a picked catalog entry seeds. Aspect/identity
 * entries mint a bare `{property, operator:'in', values:[]}` `FilterExpression`
 * (`identityEntriesPersistAsTypeAndLabel` — `type`/`label` both persist this
 * way, never a synthetic enum wrapper). Lane entries mint the matching
 * `FilterGroupLeaf`, seeded from `defaultLaneFilter` so serialization never
 * sees a widen-on-save raw lane row (SC6). A `measure` entry mints a LOCKED
 * relationship leaf on `measures_into` naming the picked aggregate directly —
 * it has no config of its own to fill in (`pickingMeasureEntryMintsLockedRelationshipRow`).
 */
export function mintFilterFromCatalogEntry(entry: CatalogEntry): FilterExpression | FilterGroupLeaf {
  switch (entry.lane) {
    case 'perspective':
      return { kind: 'perspective', filter: { ...defaultLaneFilter('perspective'), perspectiveSystemName: entry.key } };
    case 'relationship':
      return { kind: 'relationship', filter: { ...defaultLaneFilter('relationship'), relationshipSystemName: entry.key } };
    case 'arc':
      return { kind: 'arc', filter: { ...defaultLaneFilter('arc') } };
    case 'measure':
      return {
        kind: 'relationship',
        filter: {
          relationshipSystemName: 'measures_into',
          operator: 'in',
          direction: 'source',
          relatedTo: [entry.key],
        },
      };
    case 'identity':
      // Sphere Type / Sphere Family are set-membership: "is any of" (`in`), as the prototype's
      // spheretype/spherefamily operators lead with.
      return { property: entry.key, operator: 'in', values: [] };
    case 'aspect':
    default:
      // The row opens on the operator its data type leads with (text → contains, number →
      // equals, sets → in) — `defaultOperatorFor` — not a blanket `in`, which a text row's
      // own operator list does not offer. `values: []` stays the shape every operator reads.
      return { property: entry.key, operator: defaultOperatorFor(entry.type), values: [] };
  }
}

/** Where a picker's active scope came from — surfaced so the scope banner never claims a source it can't back up. */
export interface CatalogScopeResolution {
  typeSystemNames: string[];
  source: 'filter' | 'none';
}

/**
 * Resolve the in-scope sphere types from a filter tree — walking every
 * nested group (`collectFilterScopeLeaves` already unions OR branches), not
 * just the top-level `propertyFilters`. A `type`/`label` condition anywhere
 * in the tree, including nested inside an `or` group, resolves `source:
 * 'filter'`; nothing resolvable is `source: 'none'` (never fabricated).
 */
export function resolveCatalogScopeSource(
  filters: { propertyFilters?: readonly any[]; filterGroups?: readonly any[] } | undefined,
  allSchemas: SchemaForFilters[]
): CatalogScopeResolution {
  const leaves = collectFilterScopeLeaves(filters);
  const schemas = inferEligibleSchemas(leaves, allSchemas);
  return {
    typeSystemNames: schemas.map((s) => s.system_name),
    source: schemas.length > 0 ? 'filter' : 'none',
  };
}

/**
 * Every authoring surface that mounts a filter editor (OQ-1 / Gap B). Add a
 * case here — never a component-local special case — when a new surface's
 * lane availability differs from "all".
 */
export type FilterAuthoringSurface =
  | 'applicability'
  | 'context'
  | 'view'
  | 'column'
  | 'session'
  | 'factor-related-spheres';

/**
 * `allowed: undefined` is the fail-closed default (SC10) — no restriction
 * beyond whatever catalogs the host injects. `allowed: []` withholds every
 * lane; `reasonKey` is then always present, so a caller can never hide a
 * lane silently. Typed `readonly never[]` (not `FilterLaneKind[]`) because
 * the only value ever produced is empty — that makes it assignable to any
 * lane-array-typed prop, including the legacy `FilterLane[]` ones.
 */
export interface LaneAvailability {
  allowed: never[] | undefined;
  reasonKey?: string;
  /**
   * The six-lane catalog's own filter (`FilterLaneKind`, not the legacy
   * `allowed`/`FilterLane` pair above) — `undefined` means every lane the
   * catalog builds is offered. A surface that withholds lanes from its legacy
   * `allowedLanes` gate withholds the SAME lanes from the catalog; this is
   * still the one predicate (OQ-1), just carrying both vocabularies a caller
   * may need until `allowedLanes` is retired.
   */
  catalogLanes?: FilterLaneKind[];
}

/**
 * The one place lane availability is decided (OQ-1 / Gap B) — every host
 * routes through this instead of a component-local `allowedLanes={[]}`.
 * One surface still withholds lanes because its evaluator cannot read a lane
 * leaf — offering one would save a filter that is never evaluated:
 * - `applicability`: the server's `validateApplicability` is synchronous and
 *   can never evaluate a lane leaf (perspective/relationship/arc all require
 *   live graph traversal) — a lane leaf there silently matches nothing (SC13).
 *
 * `factor-related-spheres` (the Rollup Factor set card's NARROWED TO tree)
 * falls through to the default: `buildMonitoringRulesQuery` /
 * `checkIfSphereMatchesFilters` evaluate the tree's `filters` directly
 * (EAINT-876), so every lane the catalog builds — including perspective — is
 * evaluated, not just saved.
 */
export function allowedLanesForSurface(surface: FilterAuthoringSurface): LaneAvailability {
  if (surface === 'applicability') {
    return {
      allowed: [],
      reasonKey: 'context.editor.inspector.applicability.lanesUnavailableReason',
      catalogLanes: ['aspect', 'identity'],
    };
  }
  return { allowed: undefined };
}

/** Max recents shown above the family groups — the prototype's cap (`vocabulary.json`). */
export const MAX_RECENT_CATALOG_ENTRIES = 4;

/**
 * Push a freshly picked entry onto the front of the recents list, deduping
 * by (lane,key) and capping at `MAX_RECENT_CATALOG_ENTRIES` — newest first,
 * above the family groups (`recentsAppearAboveFamiliesAfterFirstPick`).
 */
export function pushRecentCatalogEntry(recents: CatalogEntry[], picked: CatalogEntry): CatalogEntry[] {
  const deduped = recents.filter((r) => !(r.key === picked.key && r.lane === picked.lane));
  return [picked, ...deduped].slice(0, MAX_RECENT_CATALOG_ENTRIES);
}
